import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { InlineTagNodeWrap } from '../card/CommonCardStyles';
import { IconName } from '../images/IconMap';
import { LabelLarge, LightXSmall } from '../text/Typography';
import { ButtonRow, RowChildrenContainer, RowContent, RowEndIcon, RowStartIcon, RowWrapper } from './CommonRowStyles';

interface IAccordionRowProps {
    title: string;
    description?: string;
    disabled?: boolean;
    startIcon?: IconName;
    iconSize?: number;
    iconColor?: string;
    tagNode?: React.ReactNode;
    children?: React.ReactNode;
    defaultOpen?: boolean;
}

export default function AccordionRow({
    title,
    description,
    disabled = false,
    startIcon,
    iconSize = 16,
    iconColor,
    tagNode,
    children,
    defaultOpen = false,
}: IAccordionRowProps) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const toggleOpen = () => {
        if (!disabled) setIsOpen((prev) => !prev);
    };

    useEffect(() => {
        setIsOpen(defaultOpen);
    }, [defaultOpen]);

    return (
        <StyledRowWrapper>
            <StyledButtonRow onClick={toggleOpen} disabled={disabled} isOpen={isOpen} role="button">
                <RowContent>
                    {tagNode ? (
                        <InlineTagNodeWrap>
                            {tagNode}{' '}
                            <ActionRowTitle as="span">
                                {startIcon && <RowStartIcon name={startIcon} size={iconSize} color={iconColor} />}
                                {title}
                            </ActionRowTitle>
                        </InlineTagNodeWrap>
                    ) : (
                        <ActionRowTitle as="span">
                            {startIcon && <RowStartIcon name={startIcon} size={iconSize} color={iconColor} />}
                            {title}
                        </ActionRowTitle>
                    )}
                    {description && <LightXSmall as="span">{description}</LightXSmall>}
                </RowContent>
                <RotatingEndIcon name="expandArrow" size={iconSize} isOpen={isOpen} />
            </StyledButtonRow>
            {isOpen && children && <StyledRowChildrenContainer>{children}</StyledRowChildrenContainer>}
        </StyledRowWrapper>
    );
}

const StyledButtonRow = styled(ButtonRow)<{ disabled: boolean; isOpen?: boolean }>`
    border-radius: ${(props) => (props.isOpen ? '10px 10px 0 0' : '10px')};
    background-color: ${(props) => props.theme.color.cardHover};

    &:hover {
        background-color: ${(props) => (props.isOpen ? props.theme.color.cardHover : props.theme.input.background)};
    }
    &:focus-visible,
    :focus {
        outline: none !important;
        background-color: ${(props) => props.theme.color.cardHover};
    }
`;

const ActionRowTitle = styled(LabelLarge)`
    color: inherit;
`;
const StyledRowChildrenContainer = styled(RowChildrenContainer)`
    background-color: ${(props) => props.theme.color.cardHover};
    padding: 0 1rem 1rem 1rem;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: -0.5rem;
`;

const RotatingEndIcon = styled(RowEndIcon)<{ isOpen: boolean }>`
    transition: transform 0.2s ease;
    transform: rotate(${(p) => (p.isOpen ? '180deg' : '0deg')});
    align-self: center;
`;

const StyledRowWrapper = styled(RowWrapper)`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 0.5rem;
    border-radius: 10px;
    border: 1px solid ${(props) => props.theme.color.cardHover};
    &:focus-within {
        outline: 2px solid ${(props) => props.theme.color.accent};
        outline-offset: -2px;
    }
`;
